import './css/App.css';
import { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

//Hooks
import axios from './api/axios';
import useAuth from './hooks/useAuth';
import useAxiosPrivate from './hooks/useAxiosPrivate';

//Context
import { useGlobalSuperAdminSettings } from './context/SuperAdminSettingsProvider';
import { useGlobalAdminSettings } from './context/AdminSettingsProvider';

//Components

import LoadingSpinner from './components/loadingSpinner/LoadingSpinner';

import Unauthorized from './components/authComponents/Unauthorized';
import PersistLogin from './components/authComponents/PersistLogin';
import RequireAuth from './components/authComponents/RequireAuth';

import Navbar from './components/mainComponents/navBar';
import NavBarBottom from './components/mainComponents/navBarBottom';
import Hamburger from './components/mainComponents/hamburger';

import Home from './components/bodyComponents/Home';
import Subscriber from './components/bodyComponents/Subscriber';

import Signin from './components/authComponents/Signin';
import Signup from './components/authComponents/Signup';
import ResetPassword from './components/authComponents/ResetPassword';

import Profile from './components/bodyComponents/Profile';
import AdminUsers from './components/bodyComponents/users/AdminUsers';
import AdminUsersBulk from "./components/bodyComponents/users/AdminUsersBulk";

import RoleChangeLog from './components/bodyComponents/users/RoleChangeLog';
import SubscribeForm from './components/bodyComponents/SubscribeForm';
import LoginHistory from './components/bodyComponents/users/LoginHistory';
import NotFound from './components/bodyComponents/NotFound';
import Footer from './components/mainComponents/footer';

import SocialAllUsers from './components/bodyComponents/socialComponents/SocialAllUsers';
import SocialFollowers from './components/bodyComponents/socialComponents/SocialFollowers';
import SocialFollowee from './components/bodyComponents/socialComponents/SocialFollowee';
import SocialMuted from './components/bodyComponents/socialComponents/SocialMuted';
import SocialPendingRequests from './components/bodyComponents/socialComponents/SocialPendingRequests';
import SocialOneUser from './components/bodyComponents/socialComponents/SocialOneUser';

import UsersWithMessages from './components/bodyComponents/messagingComponents/UsersWithMessages'
import Chat from './components/bodyComponents/messagingComponents/Chat';

import Posts from './components/bodyComponents/postsComponents/Posts';
import PostComments from './components/bodyComponents/postsComponents/PostComments';
import PostReactions from './components/bodyComponents/postsComponents/PostReactions';
import PostCommentReactions from './components/bodyComponents/postsComponents/PostCommentReactions';

import ModeratorPosts from './components/bodyComponents/moderatorComponents/ModeratorPosts';
import ModeratorPostsHistory from './components/bodyComponents/moderatorComponents/ModeratorPostsHistory';
import HiddenPosts from './components/bodyComponents/moderatorComponents/HiddenPosts'

import ModeratorComments from './components/bodyComponents/moderatorComponents/ModeratorComments';
import HiddenComments from './components/bodyComponents/moderatorComponents/HiddenComments';
import ModeratorsCommentsHistory from './components/bodyComponents/moderatorComponents/ModeratorCommentsHistory';

import SuperAdminSetup from './components/bodyComponents/adminComponents/SuperAdminSetup';
import AdminSetup from './components/bodyComponents/adminComponents/AdminSetup';
import AdminRoles from './components/bodyComponents/rolesComponents/AdminRoles';
import HomeAdmin from './components/bodyComponents/adminComponents/HomeAdmin';
import RolePage from './components/bodyComponents/RolePage';
import RoleAdminHistory from './components/bodyComponents/rolesComponents/RoleAdminHistory';

import AdminSubscription from './components/bodyComponents/adminComponents/AdminSubscription';
import AdminOverride from './components/bodyComponents/adminComponents/AdminOverride';

import StepPage from './components/bodyComponents/stepsComponents/StepPage';
import StepAdd from './components/bodyComponents/stepsComponents/StepAdd';
import StepLog from './components/bodyComponents/adminComponents/StepLog';

import AdminOrphanedSteps from './components/bodyComponents/stepsComponents/AdminOrphanedSteps';
import AdminStepsBulk from './components/bodyComponents/stepsComponents/AdminStepsBulk';
import FavouriteSteps from './components/bodyComponents/stepsComponents/FavouriteSteps';

import PrivacyPolicy from './components/bodyComponents/legal/PrivacyPolicy';
import TermsOfUse from './components/bodyComponents/legal/TermsOfUse';

import TermsModal from './components/legal/TermsModal';
import DemoModal from './components/demoModal/DemoModal';



function App() {

  const axiosPrivate = useAxiosPrivate();
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isFollowNotification, setIsFollowNotification] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false)
  const [hasMessagesAtAll, setHasMessagesAtAll] = useState(false)
  const [hasPostReports, setHasPostReports] = useState(false);
  const [hasCommentsReports, setHasCommentsReports] = useState(false);
  const [customRoles, setCustomRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([])
  const [colors, setColors] = useState({})
  const [showDemoModal, setShowDemoModal] = useState(false);

  const [loadingRoles, setLoadingRoles] = useState(false);
  // console.log(loadingRoles)

  const { auth, setAuth } = useAuth();

  // console.log('auth', auth)
  // console.log('auth planviolation', auth.planViolations)

  const isAdmin = auth?.roles?.includes("Admin");
  const isSuperAdmin = auth?.roles?.includes("SuperAdmin");

  // console.log('isAdmin', isAdmin)

  const hasViolation = auth?.planViolations?.any === true;

  // console.log('planViolations', auth?.planViolations)

  const location = useLocation();
  const currentPath = location.pathname;

  // Allowed Admin cleanup routes (DO NOT include "/")
  const allowedAdminPaths = [
    "/admin/users",
    "/admin/adminsubscription",
    "/admin/adminoverride",
    "/admin/homeadmin",
    "/admin/steps",
    "/admin/roles",
    "/admin/orphaned-steps",
  ];

  // HOME is special — allowed only when exactly "/"
  const isAdminAllowedOnHome = currentPath === "/";

  // Admin is allowed if ON HOME or ON any cleanup route
  const adminIsAllowedHere =
    isAdminAllowedOnHome ||
    allowedAdminPaths.some(path => currentPath.startsWith(path));

  // paths allowed even when over plan limits
  const violationExemptPaths = [
    "/profile/myaccount"
  ];

  const isViolationExempt =
    currentPath === "/" ||
    currentPath.startsWith("/profile/myaccount");


  // FINAL RULE:
  // - SuperAdmin always allowed
  // - Normal users blocked if violation
  // - Admin blocked UNLESS on cleanup pages
  const showPlanLock =
    hasViolation && !isViolationExempt &&
    (
      (!isAdmin && !isSuperAdmin) ||   // Normal users always blocked
      (isAdmin && !adminIsAllowedHere) // Admin blocked except cleanup pages
    );


  // console.log('currentPath', currentPath, 'adminIsAllowedHere', adminIsAllowedHere, 'hasViolation', hasViolation, 'showPlanLock', showPlanLock)

  function PlanLimitOverlay({ isAdmin }) {

    const baseStyle = {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.85)",
      zIndex: 999999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "2rem",
    };

    const boxStyle = {
      background: "white",
      borderRadius: "12px",
      padding: "2rem",
      maxWidth: "520px",
      textAlign: "center",
      boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
    };

    if (isAdmin) {
      return (
        <div style={baseStyle}>
          <div style={boxStyle}>
            <h2 style={{ marginBottom: "1rem" }}>
              Your school’s subscription limits have been exceeded.
            </h2>

            <p style={{ marginBottom: "1rem", fontSize: "1.05rem" }}>
              Some features are temporarily restricted.
              You can regain access by reducing usage or upgrading the subscription.
            </p>

            <ul style={{ textAlign: "left", margin: "1.5rem 0", fontSize: "0.95rem", lineHeight: "1.5" }}>
              <li>Delete users to reduce the user count.</li>
              <li>Delete roles to reduce the number of levels.</li>
              <li>Delete steps or media from the Home Admin panel to reduce storage usage.</li>
              <li>Or upgrade your plan in the Subscription settings.</li>
            </ul>

            <button
              onClick={() => (window.location.href = "/admin/adminsubscription")}
              style={{
                marginTop: "1rem",
                padding: "0.75rem 1.25rem",
                borderRadius: "8px",
                background: "#007bff",
                color: "white",
                border: "none",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              Open Subscription Settings
            </button>
          </div>
        </div>
      );
    }

    // Normal users (not admin)
    return (
      <div style={baseStyle}>
        <div style={boxStyle}>
          <h2 style={{ marginBottom: "1rem" }}>
            Access is on hold while the school's subscription is adjusted.
          </h2>
        </div>
      </div>
    );
  }

  useEffect(() => {
  if (!GA_MEASUREMENT_ID) return;

  if (process.env.NODE_ENV !== "production") return;

  // Prevent duplicate injection
  if (window.gtag) return;

  // Load GA script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
  });

}, []);

useEffect(() => {
  if (!GA_MEASUREMENT_ID) return;
  if (process.env.NODE_ENV !== "production") return;
  if (!window.gtag) return;

  window.gtag("event", "page_view", {
    page_path: location.pathname,
  });

}, [location.pathname]);

  useEffect(() => {
  const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';
  if (!IS_DEMO_MODE) return;

  const hasSeenDemoModal = sessionStorage.getItem('dnzify_demo_modal_seen');

  if (!hasSeenDemoModal) {
    setShowDemoModal(true);
    sessionStorage.setItem('dnzify_demo_modal_seen', 'true');
  }
}, []);



  useEffect(() => {
    const fetchColors = async () => {
      try {
        const response = await axios.get('/school/colors');

        if (response.data && Object.keys(response.data).length > 0) {

          setColors(response.data); // take first row
        } else {
          console.warn('No colors found, using defaults');
        }
      } catch (err) {
        console.error('Failed to fetch colors', err);
      }
    };

    fetchColors();
  }, []);

  useEffect(() => {
    if (Object.keys(colors).length > 0) {
      // console.log('colors in App.js', colors);
    }
  }, [colors]);

  // In App.js
  useEffect(() => {
    if (colors && Object.keys(colors).length > 0) {
      for (const [key, value] of Object.entries(colors)) {
        // key is color_0, color_1, etc.
        document.documentElement.style.setProperty(`--${key}`, value);
      }
      // console.log('CSS variables updated with colors:', colors);
    }
  }, [colors]);


  useEffect(() => {
    const fetchAllRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await axios.get('/all-roles-public');
        setAllRoles(response.data);
      } catch (err) {
        console.error('Failed to fetch all roles', err);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchAllRoles();
  }, []);

  // Update screenWidth on window resize
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [profilePictureKey, setProfilePictureKey] = useState(Date.now());
  // console.log(profilePictureKey, 'profilePictureKey')

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  useEffect(() => {
    const fetchCustomRoles = async () => {
      try {
        const response = await axios.get('/custom-roles-public');
        setCustomRoles(response.data);
      } catch (err) {
        console.error('Failed to fetch custom roles', err);
      }
    };

    fetchCustomRoles();
  }, []);

  // useEffect(() => {
  //   if (!auth?.roles?.includes("Admin")) return;



  //   const fetchStatus = async () => {
  //     try {
  //       const res = await axiosPrivate.get("/school/plan-status");
  //       setAuth(prev => ({
  //         ...prev,
  //         planViolations: res.data
  //       }));
  //     } catch (err) {
  //       console.error("Failed to update plan status", err);
  //     }
  //   };



  //   fetchStatus();

  // }, [location.pathname]);

  const { superAdminSettings, isLoading: loadingPostFeatures } = useGlobalSuperAdminSettings();
  const { adminSettings, isLoading: loadingAdminSettings } = useGlobalAdminSettings();

  if (loadingPostFeatures || loadingAdminSettings) {
    return <LoadingSpinner />;
  }


  if (process.env.NODE_ENV === 'development') {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (args[0]?.includes('You may test your Stripe.js integration over HTTP')) return;
      originalWarn(...args);
    };
  }

  //  console.log('auth in App.js', auth)

  return (

    <div className="app">


      <Navbar isNavOpen={isNavOpen} toggleNav={toggleNav} profilePictureKey={profilePictureKey} setProfilePictureKey={setProfilePictureKey} isFollowNotification={isFollowNotification} setIsFollowNotification={setIsFollowNotification} hasNewMessages={hasNewMessages} hasMessagesAtAll={hasMessagesAtAll} setHasMessagesAtAll={setHasMessagesAtAll} customRoles={customRoles} hasPostReports={hasPostReports} hasCommentsReports={hasCommentsReports} setHasCommentsReports={setHasCommentsReports} setHasPostReports={setHasPostReports} onOpenDemoModal={() => setShowDemoModal(true)} />

      <Hamburger isNavOpen={isNavOpen} toggleNav={toggleNav} />

      {showPlanLock && <PlanLimitOverlay isAdmin={isAdmin} />}

      {/* {console.log('authLoaded', auth?.authLoaded, 'auth userId != null', auth?.userId != null, 'termsAcceptedAt != null', auth?.termsAcceptedAt != null)} */}

      {auth?.authLoaded &&
        auth?.userId != null &&
        auth.termsAcceptedAt === null && (
          <TermsModal
            onAccepted={(date) =>
              setAuth(prev => ({ ...prev, termsAcceptedAt: date }))
            }
          />
        )}


      {showDemoModal && (
  <DemoModal onClose={() => setShowDemoModal(false)} />
)}



      <Routes>
        {/* Public routes */}


        <Route path="/signin" element={<Signin isNavOpen={isNavOpen} screenWidth={screenWidth} setHasNewMessages={setHasNewMessages} setHasMessagesAtAll={setHasMessagesAtAll} setHasCommentsReports={setHasCommentsReports} setHasPostReports={setHasPostReports} />} />
        <Route path="/signup" element={<Signup isNavOpen={isNavOpen} screenWidth={screenWidth} />} />
        <Route path="/" element={<Home isNavOpen={isNavOpen} />} />
        <Route path="resetpassword" element={<ResetPassword isNavOpen={isNavOpen} />} />


        <Route path="/unauthorized" element={<Unauthorized isNavOpen={isNavOpen} />} />


        <Route path="/legal/terms" element={<TermsOfUse isNavOpen={isNavOpen} />} />

        <Route path="/legal/privacy" element={<PrivacyPolicy isNavOpen={isNavOpen} />} />


        {/* Protected Routes */}
        <Route element={<PersistLogin />}>


          <Route element={<RequireAuth allowedRoles={['User_registered']} />}>
            <Route
              path="/steps/favourites"
              element={<FavouriteSteps isNavOpen={isNavOpen} customRoles={customRoles} />}
            />
          </Route>


          {customRoles?.map(role => {
            const routePath = `/steps/${role.role_slug}`;
            const allowedRoles = ['SuperAdmin', 'Admin', 'Moderator'
              , role.role_name
            ];

            return (
              <Route key={role.role_name} element={<RequireAuth allowedRoles={['SuperAdmin', 'Admin', 'Moderator', `${role.role_name}`]} />}>
                <Route
                  path={routePath}
                  element={<RolePage role={role} isNavOpen={isNavOpen} />}
                />


                <Route
                  path="/roles/:role_slug/steps/:step_id"
                  element={<StepPage isNavOpen={isNavOpen} customRoles={customRoles} />}
                />


                <Route
                  path={`${routePath}/steps/:step_id`}
                  element={<StepPage isNavOpen={isNavOpen} />}
                />


              </Route>
            );
          })}





          {
            // superAdminSettings.showPostsFeature && adminSettings.showPostsFeature &&
            <>
              <Route element={<RequireAuth allowedRoles={['Moderator']} />}>
                <Route path="/moderator/posts" element={<ModeratorPosts isNavOpen={isNavOpen} setHasPostReports={setHasPostReports} />} />
              </Route>
              <Route element={<RequireAuth allowedRoles={['Moderator']} />}>
                <Route path="/moderator/posts/history" element={<ModeratorPostsHistory isNavOpen={isNavOpen} />} />
              </Route>
              <Route element={<RequireAuth allowedRoles={['Moderator']} />}>
                <Route path="/moderator/comments/history" element={<ModeratorsCommentsHistory isNavOpen={isNavOpen} />} />
              </Route>
              <Route element={<RequireAuth allowedRoles={['Moderator']} />}>
                <Route path="/moderator/hidden/posts" element={<HiddenPosts isNavOpen={isNavOpen} />} />
              </Route>
              <Route element={<RequireAuth allowedRoles={['Moderator']} />}>
                <Route path="/moderator/comments/" element={<ModeratorComments isNavOpen={isNavOpen} />} />
              </Route>
              <Route element={<RequireAuth allowedRoles={['Moderator']} />}>
                <Route path="/moderator/hidden/comments/" element={<HiddenComments isNavOpen={isNavOpen} />} />
              </Route>
            </>
          }

          <Route element={<RequireAuth allowedRoles={['User_subscribed']} />}>
            <Route path="/subscriber" element={<Subscriber isNavOpen={isNavOpen} />} />
          </Route>
          <Route path="/subscribe" element={<SubscribeForm isNavOpen={isNavOpen} />} />


          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route path="social/allusers" element={<SocialAllUsers isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
            </Route>
          )}

          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route path="social/following" element={<SocialFollowee isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
            </Route>
          )}

          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route path="social/followers" element={<SocialFollowers isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
            </Route>
          )}

          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route path="social/muted" element={<SocialMuted isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
            </Route>
          )}

          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route path="social/pending" element={<SocialPendingRequests isNavOpen={isNavOpen} screenWidth={screenWidth} isFollowNotification={isFollowNotification} setIsFollowNotification={setIsFollowNotification} profilePictureKey={profilePictureKey} />} />
            </Route>
          )}

          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route path="social/users/:userId" element={<SocialOneUser isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
            </Route>
          )}

          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route path="messages" element={<UsersWithMessages isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
            </Route>
          )}


          {
            // superAdminSettings.showPostsFeature && adminSettings.showPostsFeature && 
            <>
              {!loadingRoles && (
                <Route element={<RequireAuth allowedRoles={allRoles} />}>
                  <Route path="posts/" element={<Posts isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
                </Route>
              )}

              {!loadingRoles && (
                <Route element={<RequireAuth allowedRoles={allRoles} />}>
                  <Route path="posts/:param" element={<PostComments isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
                </Route>
              )}


              {!loadingRoles && (
                <Route element={<RequireAuth allowedRoles={allRoles} />}>
                  <Route path="posts/reactions/:param" element={<PostReactions isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
                </Route>
              )}

              {!loadingRoles && (
                <Route element={<RequireAuth allowedRoles={allRoles} />}>
                  <Route path="posts/comments/reactions/:param" element={<PostCommentReactions isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} />} />
                </Route>
              )}
            </>
          }

          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route exact path="messages/:userId" element={<Chat isNavOpen={isNavOpen} setHasNewMessages={setHasNewMessages} profilePictureKey={profilePictureKey} />} />
            </Route>
          )}

          {!loadingRoles && (
            <Route element={<RequireAuth allowedRoles={allRoles} />}>
              <Route path="profile/myaccount" element={<Profile isNavOpen={isNavOpen} screenWidth={screenWidth} profilePictureKey={profilePictureKey} setProfilePictureKey={setProfilePictureKey} />} />
            </Route>
          )}

          {/* Admin-specific routes */}
          <Route element={<RequireAuth allowedRoles={['Admin', 'SuperAdmin']} />}>
            <Route path="/admin/users" element={<AdminUsers isNavOpen={isNavOpen} allowedRoles={['Admin', 'SuperAdmin']} customRoles={customRoles} setCustomRoles={setCustomRoles} profilePictureKey={profilePictureKey} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin', 'SuperAdmin']} />}>
            <Route path="/admin/users/bulk" element={<AdminUsersBulk isNavOpen={isNavOpen} allowedRoles={['Admin', 'SuperAdmin']} customRoles={customRoles} setCustomRoles={setCustomRoles} profilePictureKey={profilePictureKey} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin', 'SuperAdmin']} />}>
            <Route path="/admin/roles" element={<AdminRoles isNavOpen={isNavOpen} allowedRoles={['Admin', 'SuperAdmin']} customRoles={customRoles} setCustomRoles={setCustomRoles} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['SuperAdmin']} />}>
            <Route path="/admin/superadmin/setup" element={<SuperAdminSetup isNavOpen={isNavOpen} allowedRoles={['SuperAdmin']} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin']} />}>
            <Route path="/admin/admin/setup" element={<AdminSetup isNavOpen={isNavOpen} allowedRoles={['Admin']} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin']} />}>
            <Route path="/admin/homeadmin" element={<HomeAdmin isNavOpen={isNavOpen} allowedRoles={['Admin']} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin']} />}>
            <Route path="/admin/adminsubscription" element={<AdminSubscription isNavOpen={isNavOpen} allowedRoles={['Admin']} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['SuperAdmin']} />}>
            <Route path="/admin/adminoverride" element={<AdminOverride isNavOpen={isNavOpen} allowedRoles={['SuperAdmin']} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin', 'SuperAdmin']} />}>
            <Route path="/admin/superadmin/rolechangelog" element={<RoleChangeLog isNavOpen={isNavOpen} customRoles={customRoles} setCustomRoles={setCustomRoles} allowedRoles={['SuperAdmin']} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin', 'SuperAdmin']} />}>
            <Route path="/admin/superadmin/roleadminhistory" element={<RoleAdminHistory isNavOpen={isNavOpen} allowedRoles={['SuperAdmin']} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin', 'SuperAdmin']} />}>
            <Route path="/admin/superadmin/loginhistory" element={<LoginHistory isNavOpen={isNavOpen} allowedRoles={['SuperAdmin']} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin']} />}>
            <Route path="/steps/add" element={<StepAdd isNavOpen={isNavOpen} customRoles={customRoles} />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={['Admin']} />}>
            <Route path="/admin/steplog" element={<StepLog isNavOpen={isNavOpen} customRoles={customRoles} />} />

            <Route
              path="/admin/orphaned-steps"
              element={<AdminOrphanedSteps isNavOpen={isNavOpen} customRoles={customRoles} />}
            />

            <Route
              path="/admin/steps"
              element={<AdminStepsBulk isNavOpen={isNavOpen} customRoles={customRoles} />}
            />


          </Route>

        </Route>
        <Route path="*" element={<NotFound isNavOpen={isNavOpen} />} />
      </Routes>
      <Footer isNavOpen={isNavOpen} />
      <NavBarBottom isNavOpen={isNavOpen} toggleNav={toggleNav} isFollowNotification={isFollowNotification} setIsFollowNotification={setIsFollowNotification} hasNewMessages={hasNewMessages} hasMessagesAtAll={hasMessagesAtAll} setHasMessagesAtAll={setHasMessagesAtAll} setHasNewMessages={setHasNewMessages} hasPostReports={hasPostReports} hasCommentsReports={hasCommentsReports} setHasCommentsReports={setHasCommentsReports} setHasPostReports={setHasPostReports} profilePictureKey={profilePictureKey} onOpenDemoModal={() => setShowDemoModal(true)}/>

    </div>

  );
}




export default App;
